export const DEBT_SIZING_METHOD_OPTIONS = [
  { value: "fixed_debt_percent", label: "Fixed debt percent" },
  { value: "target_dscr_sizing", label: "Size debt from target DSCR" },
  { value: "lower_of_fixed_percent_and_dscr", label: "Lower of fixed debt percent and DSCR-sized debt" },
];

export const DEBT_SERVICE_FREQUENCY_OPTIONS = [
  { value: "annual", label: "Annual" },
  { value: "semiannual", label: "Semiannual (future)" },
  { value: "quarterly", label: "Quarterly (future)" },
  { value: "monthly", label: "Monthly (future)" },
];

export const DSRA_FUNDING_METHOD_OPTIONS = [
  { value: "not_modelled", label: "Not modelled" },
  { value: "upfront", label: "Upfront" },
  { value: "from_cash_flow", label: "From cash flow" },
  { value: "funded_from_debt", label: "Funded from debt proceeds" },
];

export const MORATORIUM_INTEREST_TREATMENT_OPTIONS = [
  { value: "interest_paid", label: "Interest paid" },
  { value: "interest_capitalized", label: "Interest capitalized" },
  { value: "no_interest_during_moratorium", label: "No interest during moratorium" },
];

export const INTEREST_TYPE_OPTIONS = [
  { value: "fixed", label: "Fixed rate" },
  { value: "floating_base_plus_margin", label: "Floating base plus margin" },
];

export const SCULPTING_MODE_OPTIONS = [
  { value: "sculpt_repayments_only", label: "Sculpt repayments only" },
  { value: "size_debt_to_target_dscr", label: "Size debt to target DSCR" },
];

export const FACILITY_TYPE_OPTIONS = [
  { value: "senior_term_debt", label: "Senior term debt" },
  { value: "mini_perm", label: "Mini-perm / refinancing debt" },
  { value: "bridge_loan", label: "Bridge loan" },
  { value: "construction_loan", label: "Construction loan" },
  { value: "mezzanine_debt", label: "Mezzanine / subordinated debt" },
  { value: "shareholder_loan", label: "Shareholder loan" },
  { value: "custom", label: "Custom / user-defined" },
];

export const FACILITY_STATUS_OPTIONS = [
  { value: "indicative", label: "Indicative" },
  { value: "term_sheet", label: "Term sheet" },
  { value: "committed", label: "Committed" },
  { value: "signed", label: "Signed" },
  { value: "not_specified", label: "Not specified" },
];

export const CONSTRUCTION_DRAWDOWN_MODE_OPTIONS = [
  { value: "not_modelled", label: "Not modelled" },
  { value: "single_drawdown_at_financial_close", label: "Single drawdown at financial close" },
  { value: "linear_during_construction", label: "Linear during construction period" },
  { value: "custom_drawdown_schedule_future", label: "Custom drawdown schedule (future)" },
];

export const INTEREST_DURING_CONSTRUCTION_TREATMENT_OPTIONS = [
  { value: "not_modelled", label: "Not modelled" },
  { value: "paid_currently", label: "Paid currently" },
  { value: "capitalized_to_debt", label: "Capitalized to debt" },
  { value: "included_in_capex", label: "Included in CAPEX" },
];

export const SECURITY_PACKAGE_STATUS_OPTIONS = [
  { value: "not_specified", label: "Not specified" },
  { value: "standard_project_finance_security", label: "Standard project finance security" },
  { value: "parent_guarantee_required", label: "Parent guarantee required" },
  { value: "cash_collateral_required", label: "Cash collateral required" },
  { value: "other", label: "Other" },
];

export const REFINANCING_ASSUMPTION_OPTIONS = [
  { value: "not_modelled", label: "Not modelled" },
  { value: "refinancing_required", label: "Refinancing required" },
  { value: "takeout_debt_assumed", label: "Takeout debt assumed" },
  { value: "merchant_refinancing_risk", label: "Merchant refinancing risk" },
];

export const DEFAULT_PROJECT_DEBT = {
  loanStructure: "sculpted",
  debtSizingMethod: "fixed_debt_percent",
  debtPercent: 70,
  tenorYears: 10,
  annualInterestRatePct: 5,
  moratoriumYears: 0,
  upfrontFeePct: 1,
  debtClosingCostsEur: 0,
  targetDscr: 1.3,
  minimumDscrCovenant: 1.25,
  debtServiceFrequency: "annual",
  dsraMonths: 0,
  dsraFundingMethod: "not_modelled",
  moratoriumInterestTreatment: "interest_paid",
  interestType: "fixed",
  baseRatePct: null,
  marginPct: null,
  bulletMaturityYear: null,
  balloonRepaymentPct: 100,
  sculptingMode: "sculpt_repayments_only",
  facilityType: "senior_term_debt",
  facilityStatus: "indicative",
  amortizationStartYear: null,
  constructionDrawdownMode: "not_modelled",
  constructionMonths: null,
  interestDuringConstructionTreatment: "not_modelled",
  commitmentFeePct: null,
  arrangementFeePct: null,
  securityPackageStatus: "not_specified",
  llcrCovenant: null,
  plcrCovenant: null,
  cashSweepEnabled: false,
  cashSweepPct: null,
  refinancingAssumption: "not_modelled",
  refinancingYear: null,
  customDebtScheduleEnabled: false,
};

const VALID_DEBT_SIZING_METHODS = new Set(DEBT_SIZING_METHOD_OPTIONS.map((option) => option.value));
const VALID_DEBT_SERVICE_FREQUENCIES = new Set(DEBT_SERVICE_FREQUENCY_OPTIONS.map((option) => option.value));
const VALID_DSRA_FUNDING_METHODS = new Set(DSRA_FUNDING_METHOD_OPTIONS.map((option) => option.value));
const VALID_MORATORIUM_INTEREST_TREATMENTS = new Set(
  MORATORIUM_INTEREST_TREATMENT_OPTIONS.map((option) => option.value)
);
const VALID_INTEREST_TYPES = new Set(INTEREST_TYPE_OPTIONS.map((option) => option.value));
const VALID_SCULPTING_MODES = new Set(SCULPTING_MODE_OPTIONS.map((option) => option.value));
const VALID_LOAN_STRUCTURES = new Set(["annuity", "sculpted", "equal_principal", "bullet"]);
const VALID_FACILITY_TYPES = new Set(FACILITY_TYPE_OPTIONS.map((option) => option.value));
const VALID_FACILITY_STATUSES = new Set(FACILITY_STATUS_OPTIONS.map((option) => option.value));
const VALID_CONSTRUCTION_DRAWDOWN_MODES = new Set(CONSTRUCTION_DRAWDOWN_MODE_OPTIONS.map((option) => option.value));
const VALID_INTEREST_DURING_CONSTRUCTION_TREATMENTS = new Set(INTEREST_DURING_CONSTRUCTION_TREATMENT_OPTIONS.map((option) => option.value));
const VALID_SECURITY_PACKAGE_STATUSES = new Set(SECURITY_PACKAGE_STATUS_OPTIONS.map((option) => option.value));
const VALID_REFINANCING_ASSUMPTIONS = new Set(REFINANCING_ASSUMPTION_OPTIONS.map((option) => option.value));

function parseFiniteNumber(value, fallback = 0) {
  if (value === "" || value == null) return fallback;
  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : fallback;
}

function parseOptionalFiniteNumber(value) {
  if (value === "" || value == null) return null;
  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
}

function clampPercent(value, fallback = 0) {
  const parsed = parseFiniteNumber(value, fallback);
  return Math.min(100, Math.max(0, parsed));
}

function hasOwnValue(source, key) {
  return Object.prototype.hasOwnProperty.call(source || {}, key);
}

function enumValue(value, validValues, fallback) {
  return validValues.has(value) ? value : fallback;
}

export function normalizeProjectDebt(config = {}) {
  const economics = config?.feasibility?.economics || {};
  const source =
    economics.projectDebt && typeof economics.projectDebt === "object" ? economics.projectDebt : {};
  const financialParams = economics.financialParams || {};
  const loanStructure = enumValue(
    source.loanStructure ?? financialParams.loanStructure,
    VALID_LOAN_STRUCTURES,
    DEFAULT_PROJECT_DEBT.loanStructure
  );
  const tenorYears = hasOwnValue(source, "tenorYears")
    ? parseOptionalFiniteNumber(source.tenorYears)
    : parseFiniteNumber(financialParams.tenorYears, DEFAULT_PROJECT_DEBT.tenorYears);
  const bulletMaturityYear = hasOwnValue(source, "bulletMaturityYear")
    ? parseOptionalFiniteNumber(source.bulletMaturityYear)
    : parseOptionalFiniteNumber(financialParams.bulletMaturityYear) ??
      (loanStructure === "bullet" ? tenorYears : null);
  const debtSizingMethod = enumValue(
    source.debtSizingMethod ?? financialParams.debtSizingMethod,
    VALID_DEBT_SIZING_METHODS,
    DEFAULT_PROJECT_DEBT.debtSizingMethod
  );
  const minimumDscrCovenant = hasOwnValue(source, "minimumDscrCovenant")
    ? parseOptionalFiniteNumber(source.minimumDscrCovenant)
    : parseOptionalFiniteNumber(financialParams.minimumDscrCovenant) ??
      DEFAULT_PROJECT_DEBT.minimumDscrCovenant;
  const debtPercent = hasOwnValue(source, "debtPercent")
    ? parseOptionalFiniteNumber(source.debtPercent)
    : clampPercent(financialParams.debtPercent, DEFAULT_PROJECT_DEBT.debtPercent);
  const annualInterestRatePct = hasOwnValue(source, "annualInterestRatePct")
    ? parseOptionalFiniteNumber(source.annualInterestRatePct)
    : Math.max(
        0,
        parseFiniteNumber(
          financialParams.annualInterestRatePct,
          DEFAULT_PROJECT_DEBT.annualInterestRatePct
        )
      );
  const moratoriumYears = hasOwnValue(source, "moratoriumYears")
    ? parseOptionalFiniteNumber(source.moratoriumYears)
    : Math.max(
        0,
        parseFiniteNumber(financialParams.moratoriumYears, DEFAULT_PROJECT_DEBT.moratoriumYears)
      );
  const upfrontFeePct = hasOwnValue(source, "upfrontFeePct")
    ? parseOptionalFiniteNumber(source.upfrontFeePct)
    : hasOwnValue(source, "upFrontFeePct")
      ? parseOptionalFiniteNumber(source.upFrontFeePct)
      : parseOptionalFiniteNumber(financialParams.upfrontFeePct ?? financialParams.upFrontFeePct) ??
        DEFAULT_PROJECT_DEBT.upfrontFeePct;
  const debtClosingCostsEur = hasOwnValue(source, "debtClosingCostsEur")
    ? parseOptionalFiniteNumber(source.debtClosingCostsEur)
    : parseOptionalFiniteNumber(financialParams.debtClosingCostsEur) ??
      DEFAULT_PROJECT_DEBT.debtClosingCostsEur;
  const targetDscr = hasOwnValue(source, "targetDscr")
    ? parseOptionalFiniteNumber(source.targetDscr)
    : hasOwnValue(source, "targetDSCR")
      ? parseOptionalFiniteNumber(source.targetDSCR)
      : parseOptionalFiniteNumber(financialParams.targetDscr ?? financialParams.targetDSCR) ??
        DEFAULT_PROJECT_DEBT.targetDscr;
  const dsraMonths = hasOwnValue(source, "dsraMonths")
    ? parseOptionalFiniteNumber(source.dsraMonths)
    : parseOptionalFiniteNumber(financialParams.dsraMonths) ?? DEFAULT_PROJECT_DEBT.dsraMonths;
  const balloonRepaymentPct = hasOwnValue(source, "balloonRepaymentPct")
    ? parseOptionalFiniteNumber(source.balloonRepaymentPct)
    : parseOptionalFiniteNumber(financialParams.balloonRepaymentPct) ??
      DEFAULT_PROJECT_DEBT.balloonRepaymentPct;

  return {
    ...DEFAULT_PROJECT_DEBT,
    ...source,
    loanStructure,
    debtSizingMethod,
    debtPercent: debtPercent == null ? null : clampPercent(debtPercent, DEFAULT_PROJECT_DEBT.debtPercent),
    tenorYears,
    annualInterestRatePct:
      annualInterestRatePct == null ? null : Math.max(0, annualInterestRatePct),
    moratoriumYears: moratoriumYears == null ? null : Math.max(0, moratoriumYears),
    upfrontFeePct,
    debtClosingCostsEur,
    targetDscr,
    minimumDscrCovenant,
    debtServiceFrequency: enumValue(
      source.debtServiceFrequency ?? financialParams.debtServiceFrequency,
      VALID_DEBT_SERVICE_FREQUENCIES,
      DEFAULT_PROJECT_DEBT.debtServiceFrequency
    ),
    dsraMonths,
    dsraFundingMethod: enumValue(
      source.dsraFundingMethod ?? financialParams.dsraFundingMethod,
      VALID_DSRA_FUNDING_METHODS,
      DEFAULT_PROJECT_DEBT.dsraFundingMethod
    ),
    moratoriumInterestTreatment: enumValue(
      source.moratoriumInterestTreatment ?? financialParams.moratoriumInterestTreatment,
      VALID_MORATORIUM_INTEREST_TREATMENTS,
      DEFAULT_PROJECT_DEBT.moratoriumInterestTreatment
    ),
    interestType: enumValue(
      source.interestType ?? financialParams.interestType,
      VALID_INTEREST_TYPES,
      DEFAULT_PROJECT_DEBT.interestType
    ),
    baseRatePct: parseOptionalFiniteNumber(source.baseRatePct ?? financialParams.baseRatePct),
    marginPct: parseOptionalFiniteNumber(source.marginPct ?? financialParams.marginPct),
    bulletMaturityYear,
    balloonRepaymentPct,
    sculptingMode: enumValue(
      source.sculptingMode ?? financialParams.sculptingMode,
      VALID_SCULPTING_MODES,
      DEFAULT_PROJECT_DEBT.sculptingMode
    ),
    facilityType: enumValue(source.facilityType, VALID_FACILITY_TYPES, DEFAULT_PROJECT_DEBT.facilityType),
    facilityStatus: enumValue(source.facilityStatus, VALID_FACILITY_STATUSES, DEFAULT_PROJECT_DEBT.facilityStatus),
    constructionDrawdownMode: enumValue(source.constructionDrawdownMode, VALID_CONSTRUCTION_DRAWDOWN_MODES, DEFAULT_PROJECT_DEBT.constructionDrawdownMode),
    interestDuringConstructionTreatment: enumValue(source.interestDuringConstructionTreatment, VALID_INTEREST_DURING_CONSTRUCTION_TREATMENTS, DEFAULT_PROJECT_DEBT.interestDuringConstructionTreatment),
    securityPackageStatus: enumValue(source.securityPackageStatus, VALID_SECURITY_PACKAGE_STATUSES, DEFAULT_PROJECT_DEBT.securityPackageStatus),
    refinancingAssumption: enumValue(source.refinancingAssumption, VALID_REFINANCING_ASSUMPTIONS, DEFAULT_PROJECT_DEBT.refinancingAssumption),
    amortizationStartYear: parseOptionalFiniteNumber(source.amortizationStartYear),
    constructionMonths: parseOptionalFiniteNumber(source.constructionMonths),
    commitmentFeePct: parseOptionalFiniteNumber(source.commitmentFeePct),
    arrangementFeePct: parseOptionalFiniteNumber(source.arrangementFeePct),
    llcrCovenant: parseOptionalFiniteNumber(source.llcrCovenant),
    plcrCovenant: parseOptionalFiniteNumber(source.plcrCovenant),
    cashSweepEnabled: Boolean(source.cashSweepEnabled ?? DEFAULT_PROJECT_DEBT.cashSweepEnabled),
    cashSweepPct: parseOptionalFiniteNumber(source.cashSweepPct),
    refinancingYear: parseOptionalFiniteNumber(source.refinancingYear),
    customDebtScheduleEnabled: Boolean(source.customDebtScheduleEnabled ?? DEFAULT_PROJECT_DEBT.customDebtScheduleEnabled),
  };
}

export function prepareProjectDebtForPersistence(config = {}) {
  const normalized = normalizeProjectDebt(config);
  return {
    ...normalized,
    debtPercent:
      normalized.debtPercent == null
        ? null
        : clampPercent(normalized.debtPercent, DEFAULT_PROJECT_DEBT.debtPercent),
    tenorYears:
      normalized.tenorYears == null
        ? null
        : Math.max(0, parseFiniteNumber(normalized.tenorYears, DEFAULT_PROJECT_DEBT.tenorYears)),
    annualInterestRatePct:
      normalized.annualInterestRatePct == null
        ? null
        : Math.max(
            0,
            parseFiniteNumber(
              normalized.annualInterestRatePct,
              DEFAULT_PROJECT_DEBT.annualInterestRatePct
            )
          ),
    moratoriumYears:
      normalized.moratoriumYears == null
        ? null
        : Math.max(0, parseFiniteNumber(normalized.moratoriumYears, 0)),
    upfrontFeePct: parseOptionalFiniteNumber(normalized.upfrontFeePct),
    debtClosingCostsEur: parseOptionalFiniteNumber(normalized.debtClosingCostsEur),
    targetDscr: parseOptionalFiniteNumber(normalized.targetDscr),
    minimumDscrCovenant: parseOptionalFiniteNumber(normalized.minimumDscrCovenant),
    dsraMonths: parseOptionalFiniteNumber(normalized.dsraMonths),
    baseRatePct: parseOptionalFiniteNumber(normalized.baseRatePct),
    marginPct: parseOptionalFiniteNumber(normalized.marginPct),
    bulletMaturityYear: parseOptionalFiniteNumber(normalized.bulletMaturityYear),
    balloonRepaymentPct:
      normalized.balloonRepaymentPct == null
        ? null
        : clampPercent(normalized.balloonRepaymentPct, DEFAULT_PROJECT_DEBT.balloonRepaymentPct),
    amortizationStartYear: parseOptionalFiniteNumber(normalized.amortizationStartYear),
    constructionMonths: parseOptionalFiniteNumber(normalized.constructionMonths),
    commitmentFeePct: parseOptionalFiniteNumber(normalized.commitmentFeePct),
    arrangementFeePct: parseOptionalFiniteNumber(normalized.arrangementFeePct),
    llcrCovenant: parseOptionalFiniteNumber(normalized.llcrCovenant),
    plcrCovenant: parseOptionalFiniteNumber(normalized.plcrCovenant),
    cashSweepPct: parseOptionalFiniteNumber(normalized.cashSweepPct),
    refinancingYear: parseOptionalFiniteNumber(normalized.refinancingYear),
  };
}

export function normalizeProjectDebtConfig(config = {}) {
  const projectDebt = normalizeProjectDebt(config);
  const economics = config.feasibility?.economics || {};
  const financialParams = economics.financialParams || {};
  return {
    ...config,
    feasibility: {
      ...(config.feasibility || {}),
      economics: {
        ...economics,
        projectDebt,
        financialParams: {
          ...financialParams,
          loanStructure: projectDebt.loanStructure,
          debtSizingMethod: projectDebt.debtSizingMethod,
          debtPercent: projectDebt.debtPercent,
          tenorYears: projectDebt.tenorYears,
          annualInterestRatePct: projectDebt.annualInterestRatePct,
          moratoriumYears: projectDebt.moratoriumYears,
          debtClosingCostsEur: projectDebt.debtClosingCostsEur,
          // Legacy mirrors: canonical fields are projectDebt.upfrontFeePct and projectDebt.targetDscr;
          // financialParams retains the old spellings (upFrontFeePct, targetDSCR) for backward compat only.
          upFrontFeePct: projectDebt.upfrontFeePct,
          targetDSCR: projectDebt.targetDscr,
          minimumDscrCovenant: projectDebt.minimumDscrCovenant,
          debtServiceFrequency: projectDebt.debtServiceFrequency,
          dsraMonths: projectDebt.dsraMonths,
          dsraFundingMethod: projectDebt.dsraFundingMethod,
          moratoriumInterestTreatment: projectDebt.moratoriumInterestTreatment,
          interestType: projectDebt.interestType,
          baseRatePct: projectDebt.baseRatePct,
          marginPct: projectDebt.marginPct,
          bulletMaturityYear: projectDebt.bulletMaturityYear,
          balloonRepaymentPct: projectDebt.balloonRepaymentPct,
          sculptingMode: projectDebt.sculptingMode,
        },
      },
    },
  };
}

export function prepareProjectDebtConfigForPersistence(config = {}) {
  const projectDebt = prepareProjectDebtForPersistence(config);
  const economics = config.feasibility?.economics || {};
  const financialParams = economics.financialParams || {};
  return {
    ...config,
    feasibility: {
      ...(config.feasibility || {}),
      economics: {
        ...economics,
        projectDebt,
        financialParams: {
          ...financialParams,
          loanStructure: projectDebt.loanStructure,
          debtSizingMethod: projectDebt.debtSizingMethod,
          debtPercent: projectDebt.debtPercent,
          tenorYears: projectDebt.tenorYears,
          annualInterestRatePct: projectDebt.annualInterestRatePct,
          moratoriumYears: projectDebt.moratoriumYears,
          debtClosingCostsEur: projectDebt.debtClosingCostsEur,
          upFrontFeePct: projectDebt.upfrontFeePct,
          targetDSCR: projectDebt.targetDscr,
          minimumDscrCovenant: projectDebt.minimumDscrCovenant,
          debtServiceFrequency: projectDebt.debtServiceFrequency,
          dsraMonths: projectDebt.dsraMonths,
          dsraFundingMethod: projectDebt.dsraFundingMethod,
          moratoriumInterestTreatment: projectDebt.moratoriumInterestTreatment,
          interestType: projectDebt.interestType,
          baseRatePct: projectDebt.baseRatePct,
          marginPct: projectDebt.marginPct,
          bulletMaturityYear: projectDebt.bulletMaturityYear,
          balloonRepaymentPct: projectDebt.balloonRepaymentPct,
          sculptingMode: projectDebt.sculptingMode,
        },
      },
    },
  };
}
